/**
 * WorkerSpawnButton — the on-demand "Run a worker" affordance for /agents.
 *
 * Wraps a role picker + a Run button + a transient feedback line. Posts to a
 * server action passed in via the `action` prop (kept generic so this block
 * stays decoupled from the shell's specific server-action module — same
 * pattern the kanban/task blocks use).
 *
 * Empty role list → renders an informative state instead of a useless picker.
 * Spawn returning `{ ok: false }` surfaces the reason without throwing — so
 * a "role already locked" or "unknown role" never silently fails.
 */

import * as React from "react";
import { Button } from "../../button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";

export interface WorkerSpawnRole {
  name: string;
  description: string;
}

export type WorkerSpawnResult =
  | { ok: true }
  | { ok: false; reason: string; detail?: string };

export interface WorkerSpawnButtonProps {
  roles: WorkerSpawnRole[];
  action: (role: string) => Promise<WorkerSpawnResult>;
  defaultRole?: string;
}

function reasonLabel(r: string): string {
  switch (r) {
    case "unknown-role": return "Unknown role.";
    case "locked": return "A worker is already running for this role.";
    case "spawn-failed": return "Spawn failed.";
    default: return r;
  }
}

export function WorkerSpawnButton({
  roles,
  action,
  defaultRole,
}: WorkerSpawnButtonProps) {
  const [role, setRole] = React.useState<string>(
    defaultRole && roles.some((r) => r.name === defaultRole)
      ? defaultRole
      : roles[0]?.name ?? "",
  );
  const [pending, startTransition] = React.useTransition();
  const [feedback, setFeedback] = React.useState<{
    tone: "ok" | "warn";
    text: string;
  } | null>(null);

  if (roles.length === 0) {
    return (
      <span className="type-body text-muted-foreground">
        No roles found in <code>packages/agents/roles/</code>.
      </span>
    );
  }

  function handleRun() {
    if (!role) return;
    setFeedback(null);
    startTransition(async () => {
      const res = await action(role);
      if (res.ok) {
        setFeedback({ tone: "ok", text: `Spawned ${role} — see feed below.` });
      } else {
        const detail = "detail" in res && res.detail ? `: ${res.detail}` : "";
        setFeedback({ tone: "warn", text: reasonLabel(res.reason) + detail });
      }
    });
  }

  return (
    <span className="flex items-center gap-3">
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="w-[12rem]" aria-label="Role">
          <SelectValue placeholder="Pick a role" />
        </SelectTrigger>
        <SelectContent>
          {roles.map((r) => (
            <SelectItem key={r.name} value={r.name}>
              {r.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm" onClick={handleRun} disabled={pending || !role}>
        {pending ? "Spawning…" : "Run a worker"}
      </Button>
      {feedback ? (
        <span
          className={
            feedback.tone === "ok"
              ? "type-subtext text-muted-foreground"
              : "type-subtext text-destructive"
          }
          aria-live="polite"
        >
          {feedback.text}
        </span>
      ) : null}
    </span>
  );
}
